import { useEffect, useRef } from 'react';

/**
 * Calls `fire` once the first time at least `threshold` of the returned ref's element is in view.
 * Pass `threshold = 0` to fire as soon as any pixel is visible.
 * Returns null on the server. Pass `enabled=false` to gate the observer until data is ready.
 */
export const useFireOnFirstInView = (fire: () => void, enabled = true, threshold = 0.1) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || firedRef.current || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const node = elementRef.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // The observer also calls back on the first observe() and whenever isIntersecting flips, even below the threshold.
        if (entry?.isIntersecting && entry.intersectionRatio >= threshold && !firedRef.current) {
          firedRef.current = true;
          fire();
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, fire, threshold]);

  return elementRef;
};
